// Tirar as coordenadas grandes de um IFC: trazer a geometria para junto
// da origem do ficheiro e passar a deslocação para a georreferenciação.
//
// O Revit, a exportar com as coordenadas partilhadas, põe a posição no
// mapa (no Cha139, E -40 040 e N 164 070, a cerca de 169 km da origem do
// PT-TM06) na colocação do IfcSite, e tudo o resto fica relativo a ela.
// Nesse caso basta pôr essa colocação na origem e somar a deslocação
// aos Eastings/Northings da IfcMapConversion: cada elemento fica no
// mesmo sítio do mapa, e o ficheiro deixa de ter coordenadas de mapa nas
// posições. A cota não muda (as cotas são altitudes).
//
// Só se faz quando a deslocação está toda nessa colocação. Se houver
// outra colocação absoluta longe da origem, ou pontos da geometria com
// coordenadas grandes, recusa-se: aí era preciso mexer na geometria.
// Pedido do Rui, 22/10/2026 (os IFC antigos de ARQ, MAC, HID e EEL do
// Cha139).
import { findEntityById, findEntityByType, splitTopLevelArgs, stepReal } from '@viewer/lib/stepText'
import { LIMIAR_COORDENADAS_GRANDES_M } from './coordenadasGrandes'

export type ResultadoDaRecentragem =
  /** Texto novo e a deslocação tirada (unidade do projecto). */
  | { ok: true; texto: string; dx: number; dy: number }
  /** `nada`: não havia coordenadas grandes na colocação do IfcSite. */
  | { ok: false; nada: boolean; motivo: string }

function ref(arg: string | undefined): number | null {
  const m = (arg ?? '').trim().match(/^#(\d+)$/)
  return m ? Number(m[1]) : null
}

/** Quantas vezes a entidade é referida, sem contar a própria definição. */
function usos(texto: string, id: number): number {
  return (texto.match(new RegExp(`#${id}(?!\\d)`, 'g'))?.length ?? 0) - 1
}

function coordenadas(args: string): number[] {
  return args.replace(/[()\s]/g, '').split(',').filter(Boolean).map(Number)
}

/** Recentra o IFC na origem quando as coordenadas grandes estão só na
 *  colocação (absoluta) do IfcSite. `metrosPorUnidade` é a unidade de
 *  comprimento do projecto. */
export function tirarCoordenadasGrandes(texto: string, metrosPorUnidade: number): ResultadoDaRecentragem {
  const recusa = (motivo: string): ResultadoDaRecentragem => ({ ok: false, nada: false, motivo })
  const limite = LIMIAR_COORDENADAS_GRANDES_M / metrosPorUnidade
  const grande = (v: number) => Number.isFinite(v) && Math.abs(v) > limite

  const sitio = findEntityByType(texto, 'IFCSITE')
  if (!sitio) return recusa('O ficheiro não tem IfcSite.')
  const idLocal = ref(splitTopLevelArgs(sitio.args)[5])
  const local = idLocal !== null ? findEntityById(texto, idLocal) : null
  if (!local || local.type !== 'IFCLOCALPLACEMENT') return recusa('O IfcSite não tem colocação.')
  const [relTo, rel] = splitTopLevelArgs(local.args)
  if (relTo.trim() !== '$') return recusa('A colocação do IfcSite é relativa a outra.')
  const idEixos = ref(rel)
  const eixos = idEixos !== null ? findEntityById(texto, idEixos) : null
  if (!eixos || eixos.type !== 'IFCAXIS2PLACEMENT3D') return recusa('A colocação do IfcSite não tem um IfcAxis2Placement3D.')
  const argsEixos = splitTopLevelArgs(eixos.args)
  const idPonto = ref(argsEixos[0])
  const ponto = idPonto !== null ? findEntityById(texto, idPonto) : null
  if (!ponto || ponto.type !== 'IFCCARTESIANPOINT') return recusa('A colocação do IfcSite não tem ponto de origem.')
  const [x = 0, y = 0, z] = coordenadas(ponto.args)
  if (!grande(Math.hypot(x, y))) {
    return { ok: false, nada: true, motivo: 'A colocação do IfcSite já está junto da origem.' }
  }

  // Mais nenhuma colocação absoluta longe da origem.
  for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCLOCALPLACEMENT\s*\(\s*\$\s*,\s*#(\d+)\s*\)/g)) {
    if (Number(m[1]) === idLocal) continue
    const ax = findEntityById(texto, Number(m[2]))
    const p = ax ? findEntityById(texto, ref(splitTopLevelArgs(ax.args)[0]) ?? -1) : null
    const [px = 0, py = 0] = p ? coordenadas(p.args) : []
    if (grande(Math.hypot(px, py))) {
      return recusa(`Há outra colocação absoluta longe da origem (#${m[1]}); só se recentra quando a deslocação está toda na colocação do IfcSite.`)
    }
  }
  // Nenhum ponto da geometria com coordenadas grandes.
  let pontos = 0
  for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCCARTESIANPOINT\s*\(\s*\(([^)]*)\)\s*\)/g)) {
    if (Number(m[1]) === idPonto) continue
    if (coordenadas(m[2]).some(grande)) pontos++
  }
  for (const m of texto.matchAll(/=\s*IFCCARTESIANPOINTLIST[23]D\s*\(/g)) {
    const fim = texto.indexOf(';', m.index)
    const numeros = texto.slice(m.index, fim < 0 ? undefined : fim).match(/-?\d+(?:\.\d*)?(?:E[-+]?\d+)?/gi) ?? []
    if (numeros.some((n) => grande(Number(n)))) pontos++
  }
  if (pontos > 0) {
    return recusa(`A geometria tem coordenadas grandes fora da colocação do IfcSite (${pontos} ponto${pontos === 1 ? '' : 's'} ou lista${pontos === 1 ? '' : 's'}); só se recentra sem mexer na geometria.`)
  }

  const novoPonto = `IFCCARTESIANPOINT((0.,0.${z !== undefined ? `,${stepReal(z)}` : ''}))`
  let novo: string
  if (usos(texto, idPonto!) === 1 && usos(texto, idEixos!) === 1) {
    // O ponto só serve esta colocação: muda-se no sítio.
    novo = texto.slice(0, ponto.start) + `#${idPonto}=${novoPonto};` + texto.slice(ponto.end)
  } else {
    // Partilhado: um ponto e uns eixos novos só para o IfcSite.
    let maxId = 0
    for (const m of texto.matchAll(/#(\d+)\s*=/g)) maxId = Math.max(maxId, Number(m[1]))
    const nl = texto.includes('\r\n') ? '\r\n' : '\n'
    const linhas =
      `#${maxId + 1}=${novoPonto};${nl}` +
      `#${maxId + 2}=IFCAXIS2PLACEMENT3D(#${maxId + 1},${argsEixos.slice(1).join(',')});${nl}`
    novo = texto.slice(0, local.start) + `#${idLocal}=IFCLOCALPLACEMENT($,#${maxId + 2});` + texto.slice(local.end)
    const endsec = novo.lastIndexOf('ENDSEC;')
    if (endsec < 0) return recusa('Ficheiro STEP sem ENDSEC.')
    novo = novo.slice(0, endsec) + linhas + novo.slice(endsec)
  }
  return { ok: true, texto: novo, dx: x, dy: y }
}
